import { useState } from 'react';
import AdminHeader from './components/AdminHeader';
import AdminSidebar from './components/AdminSidebar';
import CompanyList from './components/CompanyList';
import CreateCompanyModal from './components/CreateCompanyModal';
import UserManagement from './components/UserManagement';
import WhitelabelSettings from './components/WhitelabelSettings';
import PageManager from './components/PageManager';
import CodeEditor from './components/CodeEditor';
import FileManager from './components/FileManager';

const AdminPage = () => {
  const [activeSection, setActiveSection] = useState('companies');
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePasswordChange = () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }
    
    console.log('Changing password...');
    setShowPasswordModal(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    alert('Password changed successfully!');
  };

  const renderAnalytics = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Platform Analytics</h2>
        <p className="text-gray-600">Monitor platform usage and performance metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-eye-line text-blue-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Page Views</p>
              <p className="text-2xl font-bold text-gray-900">125,430</p>
              <p className="text-sm text-green-600">+12% from last month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-add-line text-green-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">New Signups</p>
              <p className="text-2xl font-bold text-gray-900">1,234</p>
              <p className="text-sm text-green-600">+8% from last month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <i className="ri-time-line text-yellow-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg. Session</p>
              <p className="text-2xl font-bold text-gray-900">24m 32s</p>
              <p className="text-sm text-red-600">-3% from last month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">$45,230</p>
              <p className="text-sm text-green-600">+15% from last month</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Trends</h3>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <i className="ri-bar-chart-line text-4xl text-gray-400 mb-2"></i>
            <p className="text-gray-500">Analytics chart would be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBilling = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Billing & Plans</h2>
        <p className="text-gray-600">Manage subscription plans and billing settings</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Basic Plan</h3>
          <p className="text-3xl font-bold text-gray-900 mb-1">$29<span className="text-lg text-gray-500">/month</span></p>
          <p className="text-gray-600 mb-4">Up to 10 users</p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Basic features</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Email support</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>5GB storage</li>
          </ul>
        </div>

        <div className="bg-white rounded-lg border-2 border-red-500 p-6 relative">
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-medium">Popular</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Professional Plan</h3>
          <p className="text-3xl font-bold text-gray-900 mb-1">$79<span className="text-lg text-gray-500">/month</span></p>
          <p className="text-gray-600 mb-4">Up to 50 users</p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>All basic features</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Priority support</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>50GB storage</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Custom branding</li>
          </ul>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Enterprise Plan</h3>
          <p className="text-3xl font-bold text-gray-900 mb-1">$199<span className="text-lg text-gray-500">/month</span></p>
          <p className="text-gray-600 mb-4">Unlimited users</p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>All pro features</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>24/7 phone support</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Unlimited storage</li>
            <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>White-label solution</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderSecurity = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Security Settings</h2>
        <p className="text-gray-600">Configure platform security and access controls</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Authentication Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Two-Factor Authentication</label>
              <p className="text-xs text-gray-500">Require 2FA for all admin accounts</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">Session Timeout</label>
              <p className="text-xs text-gray-500">Auto-logout after inactivity</p>
            </div>
            <select className="px-3 py-1 pr-8 border border-gray-300 rounded text-sm">
              <option>30 minutes</option>
              <option>1 hour</option>
              <option>2 hours</option>
              <option>4 hours</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm font-medium text-gray-700">IP Restrictions</label>
              <p className="text-xs text-gray-500">Limit access to specific IP ranges</p>
            </div>
            <button className="px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium">
              Configure
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Policy</h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <input type="checkbox" defaultChecked className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500" />
            <label className="ml-2 text-sm text-gray-700">Minimum 8 characters</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" defaultChecked className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500" />
            <label className="ml-2 text-sm text-gray-700">Require uppercase and lowercase letters</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" defaultChecked className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500" />
            <label className="ml-2 text-sm text-gray-700">Require numbers</label>
          </div>
          <div className="flex items-center">
            <input type="checkbox" className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500" />
            <label className="ml-2 text-sm text-gray-700">Require special characters</label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSystemStatus = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">System Status</h2>
        <p className="text-gray-600">Monitor platform health and performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">API Status</h3>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">99.9%</p>
          <p className="text-sm text-gray-500">Uptime</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Database</h3>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">45ms</p>
          <p className="text-sm text-gray-500">Avg Response</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Storage</h3>
            <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">78%</p>
          <p className="text-sm text-gray-500">Used</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-700">Memory</h3>
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">62%</p>
          <p className="text-sm text-gray-500">Used</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Incidents</h3>
        <div className="space-y-3">
          <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
            <i className="ri-check-line text-green-600 mr-3"></i>
            <div>
              <p className="text-sm font-medium text-gray-900">All systems operational</p>
              <p className="text-xs text-gray-500">No incidents in the last 30 days</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderActivityLogs = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Activity Logs</h2>
        <p className="text-gray-600">Monitor admin and system activities</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Recent Activities</h3>
            <div className="flex items-center space-x-3">
              <select className="px-3 py-1 pr-8 border border-gray-300 rounded text-sm">
                <option>All Activities</option>
                <option>User Actions</option>
                <option>System Events</option>
                <option>Security Events</option>
              </select>
              <button className="px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium">
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {[
            { action: 'User created', user: 'admin@teamsday.com', target: 'john@techcorp.com', time: '2 minutes ago', type: 'user' },
            { action: 'Company settings updated', user: 'admin@teamsday.com', target: 'TechCorp Solutions', time: '15 minutes ago', type: 'company' },
            { action: 'Password changed', user: 'admin@teamsday.com', target: 'Self', time: '1 hour ago', type: 'security' },
            { action: 'Whitelabel template updated', user: 'admin@teamsday.com', target: 'Default Template', time: '2 hours ago', type: 'system' },
            { action: 'User suspended', user: 'admin@teamsday.com', target: 'david@greenenergy.com', time: '1 day ago', type: 'user' }
          ].map((log, index) => (
            <div key={index} className="px-6 py-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    log.type === 'user' ? 'bg-blue-100' :
                    log.type === 'company' ? 'bg-green-100' :
                    log.type === 'security' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    <i className={`${
                      log.type === 'user' ? 'ri-user-line text-blue-600' :
                      log.type === 'company' ? 'ri-building-line text-green-600' :
                      log.type === 'security' ? 'ri-shield-line text-red-600' : 'ri-settings-line text-gray-600'
                    }`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{log.action}</p>
                    <p className="text-xs text-gray-500">by {log.user} → {log.target}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-500">{log.time}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPasswordChange = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Change Password</h2>
        <p className="text-gray-600">Update your admin account password</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Enter current password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Enter new password"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              placeholder="Confirm new password"
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex">
              <i className="ri-information-line text-blue-600 mr-2 mt-0.5"></i>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Password Requirements:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>At least 8 characters long</li>
                  <li>Include uppercase and lowercase letters</li>
                  <li>Include at least one number</li>
                  <li>Avoid common passwords</li>
                </ul>
              </div>
            </div>
          </div>

          <button
            onClick={handlePasswordChange}
            disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-lock-line mr-2"></i>
            Update Password
          </button>
        </div>
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Profile Settings</h2>
        <p className="text-gray-600">Manage your admin account information</p>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6 max-w-2xl">
        <div className="flex items-center mb-6">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
            <i className="ri-user-line text-gray-600 text-2xl"></i>
          </div>
          <div className="ml-6">
            <h3 className="text-lg font-semibold text-gray-900">Admin User</h3>
            <p className="text-gray-600">System Administrator</p>
            <button className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-700 font-medium">
              Change Avatar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              defaultValue="Admin"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              defaultValue="User"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              defaultValue="admin@teamsday.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              defaultValue="+1 (555) 123-4567"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Time Zone
            </label>
            <select className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm">
              <option>UTC-8 (Pacific Time)</option>
              <option>UTC-5 (Eastern Time)</option>
              <option>UTC+0 (GMT)</option>
              <option>UTC+1 (Central European Time)</option>
            </select>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
              <p className="text-xs text-gray-500">Receive notifications about system events</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" defaultChecked className="sr-only peer" />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap">
            Cancel
          </button>
          <button className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 whitespace-nowrap">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'companies':
        return <CompanyList onCreateCompany={() => setShowCreateCompany(true)} />;
      case 'users':
        return <UserManagement />;
      case 'pages':
        return <PageManager />;
      case 'code-editor':
        return <CodeEditor />;
      case 'file-manager':
        return <FileManager />;
      case 'whitelabel':
        return <WhitelabelSettings />;
      case 'analytics':
        return renderAnalytics();
      case 'billing':
        return renderBilling();
      case 'security':
        return renderSecurity();
      case 'system':
        return renderSystemStatus();
      case 'logs':
        return renderActivityLogs();
      case 'password':
        return renderPasswordChange();
      case 'profile':
        return renderProfile();
      default:
        return <CompanyList onCreateCompany={() => setShowCreateCompany(true)} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar activeSection={activeSection} setActiveSection={setActiveSection} />
      
      <div className="flex-1 flex flex-col">
        <AdminHeader />
        
        <main className="flex-1 p-6 overflow-y-auto">
          {renderContent()}
        </main>
      </div>

      {showCreateCompany && (
        <CreateCompanyModal onClose={() => setShowCreateCompany(false)} />
      )}
    </div>
  );
};

export default AdminPage;
